import { useEffect, useMemo, useState } from 'react';
import { rich, useT } from '@/i18n/runtime';
import { useSessionStore } from '@/store/sessionStore';
import { fetchCompletionBehaviour, SHOW_PAGE } from '@/api/session';
import { API_BASE_URL } from '@/api/client';
import type { CompletionBehaviour } from '@/api/session';
import type { StepProps } from '../types';
import { AppBar } from '../components/AppBar';
import { SecurePill } from '../components/SecurePill';
import { Button } from '../components/Button';
import { Icon } from '../components/Icon';

/**
 * SCREEN 06 · COMPLETE — calm, confident, HONEST close. Sets the true
 * expectation that activation GATES on a concurrent audit (no false "you're
 * done"); the tenant brand carries the customer home.
 */
export function Complete({ tenant }: StepProps): JSX.Element {
  const { t, locale } = useT();
  const reference = useSessionStore((s) => s.session.reference) ?? 'VCIP-7F3A-22K1';
  /*
   * CD33 A-05 — the "Thanks, ." defect on the owner's screenshot.
   *
   * `??` only catches null and undefined. The store legitimately holds an EMPTY STRING for a
   * customer whose name we never captured (a partner-triggered journey supplies a reference, not a
   * name), and an empty string sailed straight through into "Thanks, ." — a greeting with a hole in
   * it, on the last screen the customer ever sees. Trim first, then fall back: absence and
   * whitespace are the same fact here.
   */
  const storedName = useSessionStore((s) => s.session.customerName);
  const customerName =
    storedName && storedName.trim() !== '' ? storedName.trim() : t('droom.complete.nameFallback');
  const sessionId = useSessionStore((s) => s.session.sessionId);
  const initSession = useSessionStore((s) => s.initSession);
  const reset = useSessionStore((s) => s.reset);

  useEffect(() => {
    initSession({ status: 'submitted' });
  }, [initSession]);

  // V62: the tenant's flow may hand the customer back to the RE that embedded this journey.
  // SHOW_PAGE until the server says otherwise — this screen's only permitted failure mode is
  // "stayed on our page". The countdown gives the redirect a visible face: the customer sees
  // WHERE they are going and can leave now or simply wait. No silent whisking-away.
  const [completion, setCompletion] = useState<CompletionBehaviour>(SHOW_PAGE);
  const [secondsLeft, setSecondsLeft] = useState<number | null>(null);
  useEffect(() => {
    if (!sessionId) return undefined;
    let active = true;
    void fetchCompletionBehaviour(sessionId).then((c) => {
      if (!active) return;
      setCompletion(c);
      if (c.mode === 'REDIRECT') setSecondsLeft(8);
    });
    return () => {
      active = false;
    };
  }, [sessionId]);
  useEffect(() => {
    if (secondsLeft === null || completion.mode !== 'REDIRECT' || !completion.redirectUrl) {
      return undefined;
    }
    if (secondsLeft <= 0) {
      // location.assign, not replace: back returns to this page, which still shows the summary.
      window.location.assign(completion.redirectUrl);
      return undefined;
    }
    const t = window.setTimeout(() => setSecondsLeft((n) => (n === null ? null : n - 1)), 1000);
    return () => window.clearTimeout(t);
  }, [secondsLeft, completion]);

  const completedAt = useMemo(
    () =>
      new Intl.DateTimeFormat(locale === 'en' ? 'en-GB' : locale, {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        hour12: false,
        timeZone: 'Asia/Kolkata',
      }).format(new Date()),
    [locale],
  );

  return (
    <div className="app-view">
      <AppBar
        tenant={tenant}
        subtitle={t('droom.complete.subtitle')}
        right={<SecurePill kind="verified" label={t('droom.pill.verified')} />}
      />

      <div className="spacer" />

      <div style={{ textAlign: 'center' }}>
        <div className="success-mark">
          <span className="halo" />
          <span className="disc">
            <Icon name="check" size={44} />
          </span>
        </div>
        <div className="h-title" style={{ marginTop: 18 }}>
          {t('droom.complete.title')}
        </div>
        <p className="sub" style={{ maxWidth: '28ch', margin: '8px auto 0' }}>
          {t('droom.complete.sub', { name: customerName })}
        </p>
      </div>

      <div className="card summary-card" style={{ marginTop: 22 }}>
        <div className="summary-row">
          <span className="k">{t('droom.complete.k.reference')}</span>
          <span className="v">{reference}</span>
        </div>
        <div className="summary-row">
          <span className="k">{t('droom.complete.k.completed')}</span>
          <span className="v">{completedAt} IST</span>
        </div>
        <div className="summary-row">
          <span className="k">{t('droom.complete.k.status')}</span>
          <span className="v ok">{t('droom.complete.v.submitted')}</span>
        </div>
      </div>

      <div className="card pad" style={{ marginTop: 14 }}>
        <div className="next-list">
          <div className="check-row">
            <span className="ck">
              <Icon name="check" size={12} />
            </span>
            <span>{rich(t('droom.complete.next.review'))}</span>
          </div>
          <div className="check-row">
            <span className="ck">
              <Icon name="check" size={12} />
            </span>
            <span>{rich(t('droom.complete.next.confirm'))}</span>
          </div>
        </div>
      </div>

      <div className="spacer" />

      <div className="stack-sm" style={{ marginTop: 16 }}>
        {sessionId ? (
          /* Owner round 2026-08-28 item 1: the comprehensive PDF session report, downloaded
             NATIVELY from the be's public capability route (same authority as the customer-token
             bootstrap — the session id the invite link carried). A plain <a href> on purpose:
             the fetch-into-blob pattern is a recorded defect in this programme (the recording
             retrieval P0), and the customer bearer is already dead by the time this screen
             renders (a terminal session revokes it), so no header auth could work anyway. */
          <>
            <a
              className="btn btn-soft"
              href={`${API_BASE_URL}/api/v1/sessions/${encodeURIComponent(sessionId)}/report`}
              download={`vkyc-receipt-${reference}.pdf`}
            >
              {t('droom.complete.download')}
            </a>
            {/* CD33 A-05: the note beside the button, not instead of it. The document is honest
                inside — it says "submitted for review" and carries no decision — but a customer
                clicking a download on a completion screen has every reason to expect a verdict
                unless the screen says otherwise BEFORE they click. */}
            <div className="cobrand" style={{ fontSize: 11 }}>
              {t('droom.complete.downloadNote', { tenantName: tenant.name })}
            </div>
          </>
        ) : null}
        {completion.mode === 'REDIRECT' && completion.redirectUrl ? (
          <>
            <Button onClick={() => window.location.assign(completion.redirectUrl as string)}>
              {t('droom.complete.redirect.cta', { label: completion.redirectLabel ?? '' })}
            </Button>
            <div className="cobrand" style={{ fontSize: 12 }} role="status" aria-live="polite">
              {secondsLeft !== null && secondsLeft > 0
                ? t('droom.complete.redirect.countdown', {
                    label: completion.redirectLabel ?? '',
                    s: String(secondsLeft),
                  })
                : t('droom.complete.redirect.now', { label: completion.redirectLabel ?? '' })}
            </div>
          </>
        ) : (
          /* CD33 A-05: this branch has NO configured hand-back, so the button must not name one.
             It used to read "Return to {tenantName}" and call reset() — naming a destination and
             going nowhere. The REDIRECT branch above is the one that actually travels; this one
             closes the page honestly. (A tenant that wants the naming button configures a return
             destination on the branding page — CD33 B-4.) */
          <Button onClick={reset}>{t('droom.complete.return')}</Button>
        )}
        <div className="cobrand" style={{ fontSize: 11 }}>
          {rich(t('droom.complete.recorded'))}
        </div>
      </div>
    </div>
  );
}
